import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, TextField, Grid, Chip, MenuItem, Tabs, Tab, FormControlLabel, Switch, Avatar, Paper, Stack, Tooltip, Alert } from '@mui/material';
import { Add, DragIndicator, Save, Sort, Storefront } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import {
  getRestaurants, getRestaurantMenu, placeFoodOrder, placeFoodOrderFromCart,
  getFoodCart, addFoodCartItem, clearFoodCart, applyFoodCartCoupon, previewFoodCheckout,
  updateFoodOrderStatus, rateFoodOrder, updateRestaurant, deleteRestaurant,
  deleteMenuItem,
  reorderMenuItems,
} from '../../services/businessService';
import { createRestaurantWithWelcome, formatWelcomeSuccessNote } from '../../services/welcomeEmailService';
import FoodOrderLifecycleActions from '../../components/operations/FoodOrderLifecycleActions';
import {
  resolveRestaurantPhone,
  resolveRestaurantSupport,
} from '../../components/food/restaurantMasterTypes';
import RestaurantAddDrawer, {
  emptyRestaurantForm, restaurantFormToPayload, restaurantRowToForm, type RestaurantFormState,
} from '../../components/food/RestaurantAddDrawer';
import PhoneNumberField from '../../components/ui/forms/PhoneNumberField';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import GridCellTooltip from '../../components/ui/GridCellTooltip';
import RowActions from '../../components/ui/RowActions';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import EntityOperationsPanel, { QuickBookDialog } from '../../components/operations/EntityOperationsPanel';
import { responsiveDataGridSx, type ResponsiveGridColDef } from '../../components/ui/gridResponsive';
import { resolveAssetUrl } from '../../utils/assetUrl';
import { formatMoney } from '../../utils/currency';
import { getRestaurantRatingState } from '../../utils/restaurantRating';

/** Consistent restaurant thumbnail: logo when it loads, Storefront icon otherwise (never letter avatars). */
function RestaurantLogoAvatar({ logo, name }: { logo?: string; name?: string }) {
  const [failed, setFailed] = useState(false);
  const url = resolveAssetUrl(String(logo || '').trim());
  const showImage = Boolean(url) && !failed;

  useEffect(() => {
    setFailed(false);
  }, [url]);

  return (
    <Avatar
      src={showImage ? url : undefined}
      alt=""
      variant="rounded"
      slotProps={{
        img: {
          onError: () => setFailed(true),
          alt: name ? `${name} logo` : 'Restaurant logo',
        },
      }}
      sx={{
        width: 36,
        height: 36,
        bgcolor: 'action.hover',
        color: 'text.secondary',
        border: 1,
        borderColor: 'divider',
        '& .MuiSvgIcon-root': { fontSize: 20 },
      }}
    >
      <Storefront />
    </Avatar>
  );
}
const openStatusChipSx = {
  width: 'fit-content',
  maxWidth: 'none',
  flexShrink: 0,
  '& .MuiChip-label': {
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
    px: 1,
  },
} as const;

const restaurantGridSx = {
  ...responsiveDataGridSx,
  '& .MuiDataGrid-root': {
    minWidth: 920,
  },
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { height: 8 },
  '&::-webkit-scrollbar-thumb': {
    bgcolor: 'action.disabled',
    borderRadius: 4,
  },
  '& .grid-cell-badge': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .grid-cell-badge .MuiDataGrid-cellContent': {
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
  },
  '& .grid-cell-actions': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .grid-cell-actions .MuiDataGrid-cellContent': {
    overflow: 'visible',
    textOverflow: 'clip',
  },
} as const;

export default function FoodOrdersPage() {
  const queryClient = useQueryClient();
  const [bookOpen, setBookOpen] = useState(false);
  const [bookTab, setBookTab] = useState(0);
  const [cartQty, setCartQty] = useState(1);
  const [promoCode, setPromoCode] = useState('');
  const [deliveryLat, setDeliveryLat] = useState(12.9352);
  const [deliveryLng, setDeliveryLng] = useState(77.6100);

  const { data, isLoading } = useQuery({
    queryKey: ['food-orders'],
    queryFn: async () => {
      const res = await api.get('/food/orders', { params: { scope: 'live', limit: 200 } });
      return res.data;
    },
  });

  const { data: restaurants = [] } = useQuery({
    queryKey: ['restaurants-list'],
    queryFn: async () => {
      const res = await getRestaurants();
      return res?.restaurants || res || [];
    },
  });

  const restaurantId = restaurants[0]?._id;
  const { data: menu = [] } = useQuery({
    queryKey: ['menu-for-book', restaurantId],
    queryFn: () => getRestaurantMenu(restaurantId),
    enabled: !!restaurantId,
  });

  const { data: cart } = useQuery({
    queryKey: ['food-cart'],
    queryFn: getFoodCart,
    enabled: bookOpen && bookTab === 1,
  });

  const { data: checkoutPreview } = useQuery({
    queryKey: ['food-checkout-preview', deliveryLat, deliveryLng, cart?._id],
    queryFn: () => previewFoodCheckout(deliveryLat, deliveryLng),
    enabled: bookOpen && bookTab === 1 && !!cart?.items?.length,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['food-orders'] });
    queryClient.invalidateQueries({ queryKey: ['food-cart'] });
  };

  const columns: GridColDef[] = [
    { field: 'orderNumber', headerName: 'Order #', width: 130 },
    { field: 'restaurantId', headerName: 'Restaurant', flex: 1, valueGetter: (_v, row) => row.restaurantId?.name || '-' },
    { field: 'customerId', headerName: 'Customer', flex: 1, valueGetter: (_v, row) => row.customerId?.firstName || '-' },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (params) => <Chip label={(params.value as string).replace(/_/g, ' ')} size="small" />,
    },
    { field: 'total', headerName: 'Total', width: 90, valueFormatter: (v) => `${formatMoney(v)}` },
    { field: 'deliveryCharge', headerName: 'Delivery', width: 90, valueGetter: (_v, row) => `${formatMoney(row.deliveryCharge ?? 0)}` },
    { field: 'platformFee', headerName: 'Platform', width: 90, valueGetter: (_v, row) => `${formatMoney(row.platformFee ?? 0)}` },
    { field: 'paymentMethod', headerName: 'Pay', width: 80 },
    {
      field: 'actions', headerName: 'Actions', width: 340, sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
          <FoodOrderLifecycleActions order={params.row} onUpdated={invalidate} showAdminExtras />
          <EntityOperationsPanel
          entityType="food_order"
          entityId={params.row._id}
          currentStatus={params.row.status}
          terminalStatuses={['delivered', 'cancelled', 'refunded']}
          onStatusChange={async (status) => {
            await updateFoodOrderStatus(params.row._id, status);
            invalidate();
          }}
          onRate={params.row.status === 'delivered' && !params.row.rating
            ? async (rating, review) => {
              await rateFoodOrder(params.row._id, rating, review);
              invalidate();
            }
            : undefined}
        />
        </Box>
      ),
    },
    { field: 'createdAt', headerName: 'Date', width: 160, valueFormatter: (v) => new Date(v as string).toLocaleString() },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Food Delivery"
        title="Food Orders"
        subtitle="Accept orders, track kitchen-to-delivery workflow, and collect ratings."
        actions={<Button variant="contained" startIcon={<Add />} onClick={() => setBookOpen(true)}>Place Order</Button>}
      />
      <DataTableCard title="Order Queue" subtitle="End-to-end food delivery operations">
        <EnhancedDataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row._id as string}
          autoHeight
          exportFileName="food-orders"
          statusField="status"
          statusOptions={[
            { value: 'confirmed', label: 'Confirmed' },
            { value: 'preparing', label: 'Preparing' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </DataTableCard>

      <QuickBookDialog
        open={bookOpen}
        title="Place Food Order"
        onClose={() => setBookOpen(false)}
        fields={[]}
        onSubmit={async () => {}}
        hideDefaultActions
        extraContent={
          <>
            <Tabs value={bookTab} onChange={(_, v) => setBookTab(v)} sx={{ mb: 2 }}>
              <Tab label="Direct Order" />
              <Tab label="Cart Checkout" />
            </Tabs>
            {bookTab === 0 ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Restaurant" defaultValue={restaurantId} id="food-restaurant">
                    {restaurants.map((r: { _id: string; name: string }) => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Menu Item" defaultValue={menu[0]?._id} id="food-menu-item">
                    {menu.map((m: { _id: string; name: string }) => <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}><TextField fullWidth type="number" label="Quantity" defaultValue={1} id="food-qty" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Delivery Address" id="food-address" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Special Instructions" id="food-instructions" /></Grid>
                <Grid item xs={12}><PhoneNumberField fullWidth label="Contact Phone" id="food-phone" /></Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Payment" defaultValue="cash" id="food-payment">
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="wallet">Wallet</MenuItem>
                    <MenuItem value="card">Card</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={async () => {
                    const rid = (document.getElementById('food-restaurant') as HTMLInputElement)?.value || restaurantId;
                    const mid = (document.getElementById('food-menu-item') as HTMLInputElement)?.value;
                    const qty = Number((document.getElementById('food-qty') as HTMLInputElement)?.value) || 1;
                    const address = (document.getElementById('food-address') as HTMLInputElement)?.value;
                    const instructions = (document.getElementById('food-instructions') as HTMLInputElement)?.value;
                    const phone = (document.getElementById('food-phone') as HTMLInputElement)?.value;
                    const payment = (document.getElementById('food-payment') as HTMLInputElement)?.value || 'cash';
                    const item = menu.find((m: { _id: string }) => m._id === mid) || menu[0];
                    if (!item) throw new Error('No menu item');
                    await placeFoodOrder({
                      restaurantId: rid,
                      items: [{ menuItemId: item._id, quantity: qty }],
                      deliveryAddress: { street: address, city: 'Bangalore', country: 'IN' },
                      paymentMethod: payment,
                      specialInstructions: instructions || undefined,
                      contactPhone: phone || undefined,
                      lat: deliveryLat,
                      lng: deliveryLng,
                    });
                    setBookOpen(false);
                    invalidate();
                  }}>Place Order</Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Restaurant" value={restaurantId} onChange={async (e) => {
                    const rid = e.target.value;
                    const item = menu[0];
                    if (item && rid) {
                      await addFoodCartItem({
                        restaurantId: rid,
                        menuItemId: item._id,
                        name: item.name,
                        price: item.price,
                        quantity: cartQty,
                        deliveryCoords: { lat: deliveryLat, lng: deliveryLng },
                      });
                      invalidate();
                    }
                  }}>
                    {restaurants.map((r: { _id: string; name: string }) => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth label="Add to cart" value="" onChange={async (e) => {
                    const item = menu.find((m: { _id: string }) => m._id === e.target.value);
                    if (!item || !restaurantId) return;
                    await addFoodCartItem({
                      restaurantId,
                      menuItemId: item._id,
                      name: item.name,
                      price: item.price,
                      quantity: cartQty,
                      deliveryCoords: { lat: deliveryLat, lng: deliveryLng },
                    });
                    invalidate();
                  }}>
                    <MenuItem value="">Select item to add...</MenuItem>
                    {menu.map((m: { _id: string; name: string; price: number }) => <MenuItem key={m._id} value={m._id}>{m.name} — {formatMoney(m.price)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}><TextField fullWidth type="number" label="Qty" value={cartQty} onChange={(e) => setCartQty(Number(e.target.value) || 1)} /></Grid>
                <Grid item xs={8}><TextField fullWidth label="Promo" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} /></Grid>
                <Grid item xs={12}>
                  <Button size="small" onClick={() => applyFoodCartCoupon(promoCode, { lat: deliveryLat, lng: deliveryLng }).then(invalidate)}>Apply Coupon</Button>
                  <Button size="small" sx={{ ml: 1 }} onClick={() => clearFoodCart().then(invalidate)}>Clear Cart</Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">Cart items: {(cart?.items || []).map((i: { name: string; quantity: number }) => `${i.name} x${i.quantity}`).join(', ') || 'Empty'}</Typography>
                  <Typography variant="body2">Subtotal: {formatMoney(checkoutPreview?.subtotal ?? cart?.subtotal ?? 0)}</Typography>
                  <Typography variant="body2">Delivery: {formatMoney(checkoutPreview?.deliveryCharge ?? cart?.deliveryCharge ?? 0)}</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>Total: {formatMoney(checkoutPreview?.total ?? cart?.total ?? 0)}</Typography>
                </Grid>
                <Grid item xs={12}><TextField fullWidth label="Delivery Address" id="cart-address" /></Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={async () => {
                    const address = (document.getElementById('cart-address') as HTMLInputElement)?.value;
                    await placeFoodOrderFromCart({
                      deliveryAddress: { street: address, city: 'Bangalore', country: 'IN' },
                      paymentMethod: 'cash',
                      lat: deliveryLat,
                      lng: deliveryLng,
                    });
                    setBookOpen(false);
                    invalidate();
                  }}>Checkout from Cart</Button>
                </Grid>
              </Grid>
            )}
          </>
        }
      />
    </PageShell>
  );
}

interface RestaurantsPageProps {
  showHeader?: boolean;
  /** Increment from parent (e.g. Master Data header) to open the add drawer. */
  addRequestId?: number;
}

export function RestaurantsPage({ showHeader = true, addRequestId = 0 }: RestaurantsPageProps = {}) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ _id: string; name: string } | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState<RestaurantFormState>(emptyRestaurantForm);
  const [togglingActiveId, setTogglingActiveId] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['restaurants-admin'],
    queryFn: () => getRestaurants(true),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (addRequestId > 0) {
      setEditing(null);
      setForm(emptyRestaurantForm());
      setDialogOpen(true);
    }
  }, [addRequestId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = restaurantFormToPayload(form);
      if (editing?._id) {
        const { sendWelcomeEmail: _welcome, ...updatePayload } = payload;
        return updateRestaurant(editing._id as string, updatePayload);
      }
      return createRestaurantWithWelcome(payload);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['restaurants-admin'] });
      const welcomeNote = !editing && form.sendWelcomeEmail && (form.email.trim() || form.phone.trim())
        ? formatWelcomeSuccessNote(form.email.trim(), result?.welcomeNotify, result?.welcomeEmailConfig)
        : '';
      const visibilityNote = !editing
        ? (form.verificationStatus === 'pending'
          ? ' Status is Pending — this restaurant will not appear in customer apps until an admin sets it to Approved.'
          : form.verificationStatus === 'approved'
            ? ' Status is Approved — it can appear in customer apps when Active and Open.'
            : '')
        : '';
      if (welcomeNote) {
        setSaveNotice(`Restaurant created successfully.${visibilityNote}${welcomeNote}`);
      } else {
        setSaveNotice(
          editing
            ? 'Restaurant updated successfully.'
            : `Restaurant created successfully.${visibilityNote}`,
        );
      }
      setDialogOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRestaurant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurants-admin'] });
      setDeleteTarget(null);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateRestaurant(id, { isActive }),
    onMutate: ({ id }) => setTogglingActiveId(id),
    onSettled: () => setTogglingActiveId(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['restaurants-admin'] }),
  });

  const openEdit = (row: Record<string, unknown>) => {
    setEditing(row);
    setForm(restaurantRowToForm(row));
    setDialogOpen(true);
    saveMutation.reset();
  };

  const columns: ResponsiveGridColDef[] = [
    {
      field: 'logo',
      headerName: '',
      width: 56,
      sortable: false,
      renderCell: (params) => (
        <RestaurantLogoAvatar
          logo={params.row.logo as string | undefined}
          name={params.row.name as string | undefined}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 180,
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '-')} />,
    },
    { field: 'ownerName', headerName: 'Owner', width: 120, minWidth: 110, hideOnTablet: true },
    {
      field: 'slug',
      headerName: 'Slug',
      width: 160,
      minWidth: 140,
      hideOnTablet: true,
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '-')} />,
    },
    {
      field: 'verificationStatus', headerName: 'Approval', width: 110, hideOnTablet: true,
      renderCell: (params) => {
        const v = String(params.value || 'pending');
        const color = v === 'approved' || v === 'verified' ? 'success' : v === 'pending' ? 'warning' : 'error';
        return <Chip label={v} size="small" color={color as 'success' | 'warning' | 'error' | 'default'} />;
      },
    },
    {
      field: 'city', headerName: 'City', width: 110, hideOnTablet: true,
      valueGetter: (_v, row) => (row.address as { city?: string })?.city || '—',
    },
    {
      field: 'cuisineTypes',
      headerName: 'Cuisine',
      width: 180,
      minWidth: 150,
      hideOnTablet: true,
      valueGetter: (_v, row) => (row.cuisineTypes as string[])?.join(', ') || '-',
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '-')} />,
    },
    {
      field: 'revenueModel',
      headerName: 'Revenue',
      width: 130,
      minWidth: 120,
      hideOnTablet: true,
      valueGetter: (_v, row) => (row.revenueModel === 'subscription' ? 'Subscription' : 'Commission'),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '-')} />,
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 90,
      valueGetter: (_v, row) => getRestaurantRatingState(row.rating).display,
      renderCell: (p) => {
        const state = getRestaurantRatingState(p.row.rating);
        if (state.valid) {
          return (
            <Typography
              variant="body2"
              color={state.display === 'N/A' ? 'text.secondary' : 'text.primary'}
              title={state.display === 'N/A' ? 'No ratings yet' : undefined}
            >
              {state.display}
            </Typography>
          );
        }
        return (
          <Tooltip title={state.message || 'Invalid rating'} arrow placement="top">
            <Typography variant="body2" color="warning.main" sx={{ cursor: 'help' }}>
              {state.display}
            </Typography>
          </Tooltip>
        );
      },
    },
    { field: 'totalOrders', headerName: 'Orders', width: 80, hideOnTablet: true },
    {
      field: 'isOpen',
      headerName: 'Open',
      width: 120,
      minWidth: 120,
      cellClassName: 'grid-cell-badge',
      description: 'Whether the restaurant is accepting orders right now',
      valueGetter: (_v, row) => (row.isOpen === true ? 'Open' : 'Closed'),
      renderCell: (params) => {
        const open = params.row.isOpen === true;
        const label = open ? 'Open' : 'Closed';
        const tip = open
          ? 'Open now — customers can place orders'
          : 'Closed now — not accepting orders';
        return (
          <Tooltip title={tip} arrow placement="top">
            <Chip
              label={label}
              color={open ? 'success' : 'default'}
              variant={open ? 'filled' : 'outlined'}
              size="small"
              sx={openStatusChipSx}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 110,
      minWidth: 110,
      cellClassName: 'grid-cell-actions',
      description: 'Listing visibility in customer apps',
      valueGetter: (_v, row) => (row.isActive !== false ? 'Active' : 'Inactive'),
      renderCell: (params) => {
        const active = params.row.isActive !== false;
        return (
          <Tooltip
            title={active ? 'Active listing — visible in apps' : 'Inactive listing — hidden from apps'}
            arrow
            placement="top"
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Switch
                size="small"
                checked={active}
                disabled={togglingActiveId === params.row._id}
                onChange={(e) => toggleActiveMutation.mutate({
                  id: String(params.row._id),
                  isActive: e.target.checked,
                })}
                inputProps={{ 'aria-label': 'Toggle restaurant listing active status' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 52 }}>
                {active ? 'Active' : 'Inactive'}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      minWidth: 120,
      valueGetter: (_v, row) => resolveRestaurantPhone(row),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'supportNumber',
      headerName: 'Support',
      width: 130,
      minWidth: 120,
      hideOnTablet: true,
      valueGetter: (_v, row) => resolveRestaurantSupport(row),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '—')} />,
    },
    {
      field: 'vendorCommissionValue',
      headerName: 'Commission',
      width: 120,
      minWidth: 120,
      hideOnTablet: true,
      valueGetter: (_v, row) => (row.vendorCommissionType === 'fixed'
        ? formatMoney(row.vendorCommissionValue ?? 0, row)
        : `${row.vendorCommissionValue ?? 15}%`),
      renderCell: (p) => <GridCellTooltip value={String(p.value ?? '-')} />,
    },
    { field: 'minOrderAmount', headerName: 'Min Order', width: 100, hideOnTablet: true, valueFormatter: (v) => `${formatMoney(v)}` },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      minWidth: 100,
      sortable: false,
      cellClassName: 'grid-cell-actions',
      renderCell: (params) => (
        <RowActions
          editLabel="Edit restaurant"
          deleteLabel="Delete restaurant"
          onEdit={() => openEdit(params.row)}
          onDelete={() => setDeleteTarget({ _id: params.row._id, name: params.row.name })}
        />
      ),
    },
  ];

  const openAdd = () => {
    setEditing(null);
    setForm(emptyRestaurantForm());
    setDialogOpen(true);
    saveMutation.reset();
  };

  const saveFieldErrors = useMemo((): Record<string, string> => {
    if (!saveMutation.isError || !saveMutation.error) return {};
    const err = saveMutation.error as {
      response?: { data?: { errors?: Record<string, string> | unknown[] } };
    };
    const details = err.response?.data?.errors;
    if (details && typeof details === 'object' && !Array.isArray(details)) {
      return details as Record<string, string>;
    }
    return {};
  }, [saveMutation.isError, saveMutation.error]);

  const saveError = (() => {
    if (!saveMutation.isError || !saveMutation.error) return null;
    const err = saveMutation.error as {
      response?: { data?: { message?: string; errors?: unknown } };
      message?: string;
    };
    const data = err.response?.data;
    const base = data?.message || err.message || 'Failed to save restaurant';
    const details = data?.errors;
    if (Array.isArray(details) && details.length) {
      const first = details[0] as { msg?: string; message?: string; path?: string };
      const detail = first.msg || first.message || JSON.stringify(first);
      return `${base}: ${detail}`;
    }
    if (details && typeof details === 'object' && !Array.isArray(details)) {
      const firstKey = Object.keys(details)[0];
      const firstVal = (details as Record<string, string>)[firstKey];
      if (firstKey && firstVal) return `${base}: ${firstKey} — ${firstVal}`;
    }
    return base;
  })();

  const body = (
    <>
      {showHeader ? (
        <PageHeader
          eyebrow="Food Delivery"
          title="Restaurants"
          subtitle="Restaurant master — basic, business, compliance, timing, approval, commission or subscription revenue."
          actions={
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
              Add Restaurant
            </Button>
          }
        />
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Restaurants</Typography>
            <Typography variant="body2" color="text.secondary">
              Add restaurants and manage previously added locations.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Add Restaurant
          </Button>
        </Stack>
      )}
      {saveNotice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveNotice(null)}>
          {saveNotice}
        </Alert>
      )}
      <DataTableCard title="Restaurant Catalog" subtitle="Full CRUD with search, filter, and CSV export">
        <EnhancedDataGrid
          rows={data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row._id as string}
          autoHeight
          exportFileName="restaurants"
          searchPlaceholder="Search restaurants…"
          filters={[
            {
              field: 'isActive',
              label: 'Active',
              options: [
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ],
            },
            {
              field: 'isOpen',
              label: 'Open',
              options: [
                { value: 'true', label: 'Open' },
                { value: 'false', label: 'Closed' },
              ],
            },
            {
              field: 'verificationStatus',
              label: 'Approval',
              options: [
                { value: 'approved', label: 'Approved' },
                { value: 'pending', label: 'Pending' },
                { value: 'rejected', label: 'Rejected' },
              ],
            },
          ]}
          sx={restaurantGridSx}
        />
      </DataTableCard>

      <RestaurantAddDrawer
        open={dialogOpen}
        editing={editing}
        form={form}
        onFormChange={setForm}
        onClose={() => { setDialogOpen(false); saveMutation.reset(); }}
        onSubmit={() => saveMutation.mutate()}
        loading={saveMutation.isPending}
        errorMessage={saveError}
        serverFieldErrors={saveFieldErrors}
        existingRestaurants={(data || []) as Array<{ _id?: string; name?: string; slug?: string }>}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove restaurant?"
        message={`Deactivate "${deleteTarget?.name}"? It will be hidden from customer apps.`}
        confirmLabel="Remove"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
      />
    </>
  );

  return showHeader ? <PageShell>{body}</PageShell> : body;
}

export function MenuManagementPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [restaurantId, setRestaurantId] = useState(searchParams.get('restaurantId') || '');
  const [reorderMode, setReorderMode] = useState(false);
  const [orderedItems, setOrderedItems] = useState<Array<{ _id: string; name: string; category?: string; price?: number }>>([]);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => {
    const rid = searchParams.get('restaurantId');
    if (rid) setRestaurantId(rid);
  }, [searchParams]);

  const { data: restaurants = [] } = useQuery({
    queryKey: ['restaurants-admin'],
    queryFn: () => getRestaurants(true),
  });

  const { data: menu = [], isLoading } = useQuery({
    queryKey: ['menu-admin', restaurantId],
    queryFn: () => getRestaurantMenu(restaurantId, true),
    enabled: !!restaurantId,
  });

  useEffect(() => {
    setOrderedItems(menu);
  }, [menu]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMenuItem(restaurantId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['menu-admin', restaurantId] }),
  });

  const reorderMutation = useMutation({
    mutationFn: (itemIds: string[]) => reorderMenuItems(restaurantId, itemIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-admin', restaurantId] });
      setReorderMode(false);
    },
  });

  const handleDrop = useCallback((targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const list = [...orderedItems];
    const from = list.findIndex((i) => i._id === dragId);
    const to = list.findIndex((i) => i._id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setOrderedItems(list);
    setDragId(null);
  }, [dragId, orderedItems]);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Food Name', flex: 1, minWidth: 160 },
    {
      field: 'foodCode', headerName: 'Code', width: 120,
      valueGetter: (_v, row) => (row.metadata as { foodCode?: string })?.foodCode || (row.metadata as { wizard?: { foodCode?: string } })?.wizard?.foodCode || '—',
    },
    { field: 'category', headerName: 'Category', width: 120 },
    { field: 'price', headerName: 'Price', width: 90, valueFormatter: (v) => `${formatMoney(v)}` },
    { field: 'discountedPrice', headerName: 'Offer', width: 90, valueFormatter: (v) => v ? `${formatMoney(v)}` : '—' },
    {
      field: 'variants', headerName: 'Variants', width: 90,
      valueGetter: (_v, row) => ((row.variants as unknown[]) || []).length,
    },
    {
      field: 'isVeg', headerName: 'Veg', width: 70,
      renderCell: (p) => <Chip size="small" label={p.value ? 'Veg' : 'Non-Veg'} color={p.value ? 'success' : 'error'} variant="outlined" />,
    },
    {
      field: 'isAvailable', headerName: 'Available', width: 100,
      renderCell: (p) => <Chip size="small" label={p.value !== false ? 'Yes' : 'No'} />,
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (params) => (
        <RowActions
          onEdit={() => navigate(`/food/items/${params.row._id}/edit?restaurantId=${restaurantId}`)}
          onDelete={() => deleteMutation.mutate(params.row._id)}
        />
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Food Delivery"
        title="Food Catalog"
        subtitle="6-tab Food Add flow — variants, addons, tax, availability. No inventory or discounts."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Sort />}
              disabled={!restaurantId || menu.length < 2}
              onClick={() => setReorderMode((v) => !v)}
            >
              {reorderMode ? 'Cancel Reorder' : 'Reorder Menu'}
            </Button>
            <Button
              variant="outlined"
              disabled={!restaurantId}
              onClick={() => navigate(`/food/addon-groups?restaurantId=${restaurantId}`)}
            >
              Addon Groups
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={!restaurantId}
              onClick={() => navigate(`/food/items/new?restaurantId=${restaurantId}`)}
            >
              Add Food
            </Button>
          </Stack>
        }
      />
      <DataTableCard title="Food Items" subtitle="Full product records with variants, addons, and metadata from Food Add Wizard">
        <TextField select label="Restaurant" value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} sx={{ mb: 2, minWidth: 280 }}>
          {restaurants.map((r: { _id: string; name: string }) => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
        </TextField>
        {!restaurantId && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a restaurant, then use Add Food Wizard for the complete 8-step flow.</Typography>
        )}
        {reorderMode && restaurantId && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Drag items to set display order on the customer app menu.
            </Typography>
            <Stack spacing={1}>
              {orderedItems.map((item) => (
                <Paper
                  key={item._id}
                  variant="outlined"
                  draggable
                  onDragStart={() => setDragId(item._id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(item._id)}
                  sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'grab' }}
                >
                  <DragIndicator color="action" fontSize="small" />
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.category || 'Uncategorized'} · {formatMoney(item.price ?? 0)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
            <Button
              variant="contained"
              startIcon={<Save />}
              sx={{ mt: 2 }}
              disabled={reorderMutation.isPending}
              onClick={() => reorderMutation.mutate(orderedItems.map((i) => i._id))}
            >
              Save Order
            </Button>
          </Box>
        )}
        {!reorderMode && (
          <EnhancedDataGrid rows={menu} columns={columns} loading={isLoading} getRowId={(r) => r._id as string} autoHeight exportFileName="food-catalog" />
        )}
      </DataTableCard>
    </PageShell>
  );
}
